/**
 * Shared types for Onboarding Requirements
 * Used by both OnboardingSettings and CommenceHiring
 */

export interface RequirementItem {
  id: string;
  name: string;
  description: string;
  lead: string;
}

export interface RequirementCategory {
  id: string;
  name: string;
  description: string;
  icon: "monitor" | "wrench" | "shield" | "smartphone" | "file";
  iconColor: string;
  iconBg: string;
  items: RequirementItem[];
}

export const INITIAL_CATEGORIES: RequirementCategory[] = [
  {
    id: "it-equipment",
    name: "IT Equipment",
    description: "Computing and technology equipment",
    icon: "monitor",
    iconColor: "text-blue-600",
    iconBg: "bg-blue-50",
    items: [
      { id: "laptop", name: "Laptop", description: "Standard work laptop", lead: "IT" },
      { id: "desktop", name: "Desktop Computer", description: "Workstation setup", lead: "IT" },
    ],
  },
  {
    id: "tools",
    name: "Tools",
    description: "Mobile phones and tablets",
    icon: "smartphone",
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-50",
    items: [
      { id: "mobile-phone", name: "Mobile Phone", description: "Company mobile device", lead: "IT" },
    ],
  },
  {
    id: "security",
    name: "Security",
    description: "Access control and security",
    icon: "shield",
    iconColor: "text-red-500",
    iconBg: "bg-red-50",
    items: [
      { id: "swipe-card", name: "Swipe Card", description: "Building access card", lead: "HR" },
      { id: "pin-code", name: "PIN Code", description: "System access PIN", lead: "IT" },
    ],
  },
];

export const LEAD_OPTIONS = ["IT", "HR", "Finance", "Manager", "Admin"];

// ===================================================================
// Icon colors + API mapping helpers (shared by OnboardingSettings and CommenceHiring)
// ===================================================================
export const REQUIREMENT_ICON_COLORS: Record<string, { color: string; bg: string }> = {
  monitor: { color: "text-blue-600", bg: "bg-blue-50" },
  smartphone: { color: "text-emerald-600", bg: "bg-emerald-50" },
  shield: { color: "text-red-500", bg: "bg-red-50" },
  wrench: { color: "text-amber-600", bg: "bg-amber-50" },
  file: { color: "text-gray-600", bg: "bg-gray-100" },
};

export const getRequirementIconColors = (icon: string) =>
  REQUIREMENT_ICON_COLORS[icon] || REQUIREMENT_ICON_COLORS.file;

// Maps an API category row to the shape the pages render. Ids are stored
// as strings to match the shared RequirementCategory type.
export const mapRequirementCategoryFromApi = (cat: any): RequirementCategory => {
  const icon = (
    REQUIREMENT_ICON_COLORS[cat.icon] ? cat.icon : "file"
  ) as RequirementCategory["icon"];
  const colors = getRequirementIconColors(icon);
  return {
    id: String(cat.id),
    name: cat.name,
    description: cat.description || "",
    icon,
    iconColor: colors.color,
    iconBg: colors.bg,
    items: (cat.items || []).map((item: any): RequirementItem => ({
      id: String(item.id),
      name: item.name,
      description: item.description || "",
      lead: item.lead || "",
    })),
  };
};
